import type { Row, RowData } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect } from "react";

import { Count } from "@/components/shared/data-table/total-count";
import { cn } from "@/lib/utils";

type ExpandRowProps<T extends RowData> = {
  row: Row<T>;
  showCount?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
};

export function ExpandRow<T extends RowData>({
  row,
  showCount = true,
  className,
}: ExpandRowProps<T>) {
  useEffect(() => {
    if (row.getIsExpanded() && row.subRows?.length === 1) {
      row.getToggleExpandedHandler()();
    }
  }, [row.subRows, row.getIsExpanded, row.getToggleExpandedHandler]);

  return (
    <div className="flex items-center">
      {row.getCanExpand() && row.subRows.length > 1 ? (
        <ExpandButton row={row} showCount={showCount} className={className} />
      ) : (
        <div className="flex items-center justify-center">
          {showCount && row.subRows?.length > 0 && (
            <Count count={row.subRows.length} noSuccess />
          )}
        </div>
      )}
    </div>
  );
}

export function ExpandButton<T extends RowData>({
  row,
  showCount,
  className,
  children,
}: React.PropsWithChildren<ExpandRowProps<T>>) {
  useEffect(() => {
    if (row.getIsExpanded() && row.subRows?.length === 1) {
      row.getToggleExpandedHandler()();
    }
  }, [row.subRows, row.getIsExpanded, row.getToggleExpandedHandler]);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        row.getToggleExpandedHandler()();
      }}
      style={{ cursor: "pointer" }}
      className={cn("flex flex-row gap-0.5 items-center", className)}
    >
      {showCount && showCount && <Count count={row.subRows.length} noSuccess />}
      {row.getIsExpanded() ? (
        <ChevronDown size={18} />
      ) : (
        <ChevronRight size={18} />
      )}
      {children}
    </button>
  );
}
